import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import lodash from 'lodash';
import { shallowEqual, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import formBuilder from 'ui-blocks/form-builder';
import { access } from 'constants/index';
import { Col } from 'reactstrap';
import { ButtonsSubmitExport } from 'ui';
import config from './formConfig';
import authUtil from '../../../../util/auth';
import FormDisplay from './FormDisplay';

const formBuilderBag = formBuilder.build(config);
const { withComponentWrapper } = formBuilderBag;

const appAdminAccess = access.getAdminAccessByApp.sponsoredAds;

const CampaignManagerForm = (props) => {
   const { mode } = useParams();
   const [modeLabel, setModeLabel] = useState();
   const [isCpa, setIsCpa] = useState(false);
   const [showAdvertiserField, setShowAdvertiserField] = useState(false);
   const [showApprovalField, setShowApprovalField] = useState(false);
   const history = useHistory();

   const handleCPAChange = (value) => {
      setIsCpa(value);
   };

   const userPartnerRoleList = useSelector(
      (state) => (state?.authReducer?.loggedInUserPartner?.roles || []),
      shallowEqual,
   );

   const [validationSchema, setValidationSchema] = useState(undefined);

   useEffect(() => {
      const innerValidationSchema = {
         campaign: Yup.string().required('Required!'),
         paymentTypeValue: Yup.string().required('Required!'),
         dailyBudget: Yup.string().required('Required!'),
         monthlyBudget: Yup.string().required('Required!'),
         keywords: Yup.array().min(1).required('Must choose at least one keyword!'),
         title: Yup.string().required('Required!'),
         linkingUrl: Yup.string().required('Required!'),
         line1: Yup.string().required('Required!'),
         displayUrl: Yup.string().required('Required!'),
      };

      if (authUtil.hasAuthAccess(appAdminAccess)) {
         if (mode === 'create') {
            setShowAdvertiserField(true);
            innerValidationSchema.advertiser = Yup.object().shape({
               id: Yup.number().required(),
               name: Yup.string().required(),
            }).required('Required!');
         }
         if (mode === 'edit') {
            setShowApprovalField(true);
         }
      }

      setValidationSchema(Yup.object().shape(innerValidationSchema));
   }, [userPartnerRoleList]);

   useEffect(() => {
      let newModeLabel;
      switch (mode) {
         case 'create':
            newModeLabel = 'Create';
            break;
         case 'edit':
            newModeLabel = 'Edit';
            break;
         default:
            newModeLabel = '??';
      }
      setModeLabel(newModeLabel);
   }, [mode]);

   useEffect(() => {
      setIsCpa(props.initialValues.paymentType);
   }, [props.initialValues.paymentType]);

   return (
      <Formik
         enableReinitialize
         initialValues={props.initialValues}
         validationSchema={validationSchema}
         validateOnChange={ false }
         validateOnBlur={ false }
         onSubmit={ (values, { setSubmitting, setErrors }) => {
            const {
               device,
               campaign,
               keywords,
               paymentTypeValue,
               paymentType,
               dailyBudget,
               monthlyBudget,
               location,
               exclude,
               radius,
               title,
               line1,
               linkingUrl,
               displayUrl,
               approval,
               active,
            } = values;
            const selectedKeywordsPayload = keywords.map((keyword) => ({ id: parseInt(keyword.id, 10), name: keyword.label }));

            const payload = {
               device: device?.value,
               campaign,
               keywords: selectedKeywordsPayload,
               dailyBudget: parseFloat(dailyBudget),
               monthlyBudget: parseFloat(monthlyBudget),
               whitelistGeoStates: location,
               blacklistGeoStates: exclude,
               radius: radius?.value,
               title,
               line1,
               linkingUrl,
               displayUrl,
               active,
            };

            payload.paymentType = paymentType ? 'cpa' : 'cpc';
            payload[payload.paymentType] = parseFloat(paymentTypeValue);
            payload[paymentType ? 'cpc' : 'cpa'] = 0;

            const { create, edit } = config.settings.external.api.save;
            let apiCall = null;

            const isModeCreate = mode === 'create';
            if (isModeCreate) {
               apiCall = create;
               if (showAdvertiserField) {
                  payload.advertiser = { id: values?.advertiser?.id, name: values?.advertiser?.name };
               }
            } else if (mode === 'edit') {
               apiCall = edit;
               payload.advertiser = props.initialValues?.advertiser;
               payload.campaignId = props.initialValues?.campaignId;
               payload.creativeId = props.initialValues?.creativeId;
               payload.status = approval || 'PENDING';
            }

            if (apiCall !== null && lodash.isFunction(apiCall)) {
               apiCall(payload).then((res) => {
                  const data = res?.data;
                  const id = data?.data?.campaign?.id;
                  const name = data?.data?.campaign?.name;
                  setSubmitting(true);
                  if (data?.data?.campaign?.id) {
                     if (isModeCreate) {
                        toastr.success('Campaign Created Successfully!');
                        history.replace(
                           `${ config.settings.internal.routing.basePath }?campaign.id=${ id }&campaign.name=${ name }`,
                        );
                     } else {
                        toastr.success('Update Success!');
                        history.replace(
                           `${ config.settings.internal.routing.basePath }?campaign.id=${ id }&campaign.name=${ name }`,
                        );
                     }
                  } else if (lodash.isPlainObject(data?.error)) {
                     setErrors(data.error);
                  } else {
                     toastr.error('Something went wrong. Please try again later.');
                  }
               }).catch(() => {
                  toastr.error('Something went wrong. Please try again later.');
               });
            } else {
               console.error('No mode matching.');
            }
         } }
      >
         {
            (formik) => {
               return (
                  <Form>
                     <h4 className='mb-4'>
                        {modeLabel}
                        &nbsp;Campaign
                     </h4>
                     <FormDisplay
                        formik={formik}
                        showAdvertiserField={showAdvertiserField}
                        handleCPAChange={handleCPAChange}
                        isCpa={isCpa}
                        showApprovalField={showApprovalField}
                     />
                     <Col xs={8}>
                        <div className='text-center mt-4'>
                           <ButtonsSubmitExport handleSubmit={formik.handleSubmit}/>
                        </div>
                     </Col>
                  </Form>
               );
            }
         }
      </Formik>
   );
};

CampaignManagerForm.propTypes = {
   initialValues: PropTypes.objectOf(PropTypes.any).isRequired,
   // reloadInitialValues: PropTypes.func.isRequired,
};

export const { path } = formBuilderBag;
export default withComponentWrapper(CampaignManagerForm);
